import { buttonVariants, Container, Section } from '@resolute/ui';
import { calculateAffordability, formatCurrency, formatINRCompact } from '@resolute/utils';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';


import { CalcLayout, ResultStat, SliderField, ToolDisclaimer } from './components/calc-ui';

import { Seo } from '@/lib/seo';


export default function AffordabilityCalculatorPage() {
  const [income, setIncome] = useState(180_000);
  const [obligations, setObligations] = useState(15_000);
  const [foirPct, setFoirPct] = useState(50);
  const [rate, setRate] = useState(8.5);
  const [tenureYears, setTenureYears] = useState(20);
  const [downPayment, setDownPayment] = useState(3_000_000);

  const { affordableEmi, maxLoan } = useMemo(
    () =>
      calculateAffordability({
        monthlyIncome: income,
        monthlyObligations: obligations,
        foir: foirPct / 100,
        annualRatePct: rate,
        tenureMonths: tenureYears * 12,
      }),
    [income, obligations, foirPct, rate, tenureYears],
  );

  const budget = maxLoan + downPayment;

  return (
    <Section>
      <Seo
        title="Home Affordability Calculator"
        description="Find out how much home you can afford based on your income, obligations and down payment. Free affordability calculator for Indian home buyers."
        path="/calculators/affordability"
      />
      <Container className="max-w-5xl">
        <header className="mb-8">
          <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">
            How much home can you afford?
          </h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Based on your income, existing EMIs and the share of income lenders allow towards a loan
            (FOIR), here&apos;s your realistic property budget.
          </p>
        </header>

        <CalcLayout
          controls={
            <>
              <SliderField
                label="Gross monthly income"
                value={income}
                onChange={setIncome}
                min={25_000}
                max={2_000_000}
                step={5_000}
                display={formatINRCompact}
              />
              <SliderField
                label="Existing monthly EMIs"
                value={obligations}
                onChange={setObligations}
                min={0}
                max={500_000}
                step={1_000}
                display={formatINRCompact}
              />
              <SliderField
                label="FOIR (income share to EMI)"
                value={foirPct}
                onChange={setFoirPct}
                min={30}
                max={65}
                step={1}
                display={(v) => `${v}%`}
                hint="Most lenders cap total EMIs at 40–55% of income."
              />
              <SliderField
                label="Interest rate (p.a.)"
                value={rate}
                onChange={setRate}
                min={5}
                max={15}
                step={0.05}
                display={(v) => `${v.toFixed(2)}%`}
              />
              <SliderField
                label="Loan tenure"
                value={tenureYears}
                onChange={setTenureYears}
                min={1}
                max={30}
                step={1}
                display={(v) => `${v} ${v === 1 ? 'year' : 'years'}`}
              />
              <SliderField
                label="Down payment (savings)"
                value={downPayment}
                onChange={setDownPayment}
                min={0}
                max={50_000_000}
                step={100_000}
                display={formatINRCompact}
              />
            </>
          }
          results={
            <>
              <div>
                <ResultStat label="You can target a home up to" value={formatCurrency(budget)} emphasis />
              </div>

              <dl className="grid grid-cols-2 gap-4">
                <ResultStat label="Max loan eligibility" value={formatINRCompact(maxLoan)} tone="foreground" />
                <ResultStat label="Affordable EMI" value={formatCurrency(affordableEmi)} tone="foreground" />
                <ResultStat label="Down payment" value={formatINRCompact(downPayment)} tone="foreground" />
                <ResultStat
                  label="Loan-to-value"
                  value={`${budget > 0 ? Math.round((maxLoan / budget) * 100) : 0}%`}
                  tone="foreground"
                />
              </dl>

              <div className="flex flex-wrap gap-3">
                <Link
                  to={`/search?priceMax=${Math.round(budget)}`}
                  className={buttonVariants({ variant: 'primary', size: 'sm' })}
                >
                  See homes in budget
                </Link>
                <Link to="/calculators/emi" className={buttonVariants({ variant: 'outline', size: 'sm' })}>
                  Estimate the EMI
                </Link>
              </div>
              <ToolDisclaimer />
            </>
          }
        />
      </Container>
    </Section>
  );
}
